import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DataTable from "components/common/tables/DataTable";
import { usePageTitle } from "context/PageTitleContext";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePermissions } from "hooks/usePermissions";
import {
  catalogApi,
  catalogConfigs,
  normalizeResponseError,
} from "../../services/catalogs/systemCatalog.service";
import CatalogFormDialog from "./CatalogFormDialog";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import { AppButton } from "components/common/buttons";
import AppSnackbar from "components/common/snackbar/AppSnackbar";

const permissionByType = {
  contractGroups: { create: PERMISSIONS.SYSTEM.CONTRACT_GROUP_CREATE, update: PERMISSIONS.SYSTEM.CONTRACT_GROUP_UPDATE, delete: PERMISSIONS.SYSTEM.CONTRACT_GROUP_DELETE },
  contractors: { create: PERMISSIONS.SYSTEM.CONTRACTOR_CREATE, update: PERMISSIONS.SYSTEM.CONTRACTOR_UPDATE, delete: PERMISSIONS.SYSTEM.CONTRACTOR_DELETE },
  clients: { create: PERMISSIONS.SYSTEM.CLIENT_CREATE, update: PERMISSIONS.SYSTEM.CLIENT_UPDATE, delete: PERMISSIONS.SYSTEM.CLIENT_DELETE },
  subcontractGroups: { create: PERMISSIONS.SYSTEM.CONTRACT_GROUP_CREATE, update: PERMISSIONS.SYSTEM.CONTRACT_GROUP_UPDATE, delete: PERMISSIONS.SYSTEM.CONTRACT_GROUP_DELETE },
  roleInContracts: { create: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_DELETE },
  roleInContract: { create: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_CONTRACT_DELETE },
  unitInContracts: { create: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_DELETE },
  unitInContract: { create: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_CONTRACT_DELETE },
  roleInSubcontracts: { create: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_DELETE },
  roleInSubcontract: { create: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.ROLE_IN_SUBCONTRACT_DELETE },
  unitInSubcontracts: { create: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_DELETE },
  unitInSubcontract: { create: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_CREATE, update: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_UPDATE, delete: PERMISSIONS.SYSTEM.UNIT_IN_SUBCONTRACT_DELETE },
};

const CatalogManagement = ({ type, pageTitle }) => {
  const config = catalogConfigs[type];
  const permissions = usePermissions();
  const actionPermissions = permissionByType[type] || {};
  const canCreate = permissions.can(actionPermissions.create);
  const canUpdate = permissions.can(actionPermissions.update);
  const canDelete = permissions.can(actionPermissions.delete);
  const canMutate = canUpdate || canDelete;
  const { setPageTitle } = usePageTitle();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({ open: false, mode: "create", row: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await catalogApi.list(config.endpoint, {
        pageSize: 1000,
        pageNumber: 1,
        sortDirection: "ASC",
      });
      setRows(data.map(config.normalize));
    } catch (error) {
      setRows([]);
      showSnackbar(normalizeResponseError(error, `Không thể tải danh sách ${config.title}`), "error");
    } finally {
      setLoading(false);
    }
  }, [config, showSnackbar]);

  useEffect(() => {
    setPageTitle(pageTitle);
  }, [pageTitle, setPageTitle]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = config.toPayload(values);
      if (formState.mode === "edit") {
        await catalogApi.update(config.endpoint, formState.row[config.idKey], payload);
        showSnackbar(`Cập nhật ${config.title} thành công`);
      } else {
        await catalogApi.create(config.endpoint, payload);
        showSnackbar(`Thêm ${config.title} thành công`);
      }
      setFormState({ open: false, mode: "create", row: null });
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, `Không thể lưu ${config.title}`), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await catalogApi.remove(config.endpoint, deleteTarget[config.idKey]);
      setDeleteTarget(null);
      showSnackbar(`Xóa ${config.title} thành công`);
      await loadData();
    } catch (error) {
      showSnackbar(normalizeResponseError(error, `Không thể xóa ${config.title}`), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(() => config.columns, [config.columns]);

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "calc(100vh - 64px)" }}>
      <Stack spacing={2}>
        <DataTable
          columns={columns}
          data={rows}
          enableRowActions={canMutate}
          state={{ isLoading: loading, showProgressBars: submitting }}
          renderTopToolbarExtraActions={() => canCreate ? (
            <Tooltip title={config.addLabel}>
              <AppButton
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setFormState({ open: true, mode: "create", row: null })}
                disableElevation
              >
                {config.addLabel}
              </AppButton>
            </Tooltip>
          ) : null}
          renderRowActions={({ row }) => (
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <Tooltip title="Sửa">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setFormState({ open: true, mode: "edit", row: row.original })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Xóa">
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        />
      </Stack>

      <CatalogFormDialog
        open={formState.open}
        title={formState.mode === "edit" ? `Cập nhật ${config.title}` : config.addLabel}
        fields={config.fields}
        initialValues={formState.row}
        submitting={submitting}
        onClose={() => setFormState({ open: false, mode: "create", row: null })}
        onSubmit={handleSubmit}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        submitting={submitting}
        message={`Bạn có chắc chắn muốn xóa ${config.title} này?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      {/* <AppSnackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </AppSnackbar> */}

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
};

export default CatalogManagement;
